import {
  API_THREAT_TYPE_TO_ENTITY,
  ApiThreatType,
  BlockDirection,
  BlockProtocol,
  SecurityDisposeStatus,
  ThreatEntityType
} from './constants/enums'
import { deriveDisposeFlags } from './handle-result'
import { parseHitAttackTacticIds, getAttackTacticName } from './attack-tactic-map'
import type {
  ProcessChainNode,
  SecurityEventDetail,
  SecurityEventListItem,
  TargetObjectItem,
  TargetObjectFields
} from './types'
import type {
  SafeEventDetailApiResponse,
  SafeEventDetailOperatorData,
  SafeEventDetailProcessChain,
  SafeEventDetailTarget,
  SdkObjectData,
  SdkSecurityEventModel,
  SdkTargetItem,
  SdkThreat
} from './types/sdk-event-model'
import { getActionName, NodeType } from './object-type-map'
import { getEventTypeName, getEventDescByEventId } from './event-type-map'
import { StatusColor } from '@/services/parse-status.service'

const OBJECT_TYPE_KEY: Record<number, string> = {
  1: 'securityEvent.objectType.1',
  2: 'securityEvent.objectType.2',
  3: 'securityEvent.objectType.3',
  4: 'securityEvent.objectType.4',
  5: 'securityEvent.objectType.5',
  6: 'securityEvent.objectType.6',
  7: 'securityEvent.objectType.7',
  8: 'securityEvent.objectType.8',
  10: 'securityEvent.objectType.10',
  11: 'securityEvent.objectType.11',
  12: 'securityEvent.objectType.12',
  13: 'securityEvent.objectType.13',
  14: 'securityEvent.objectType.14',
  15: 'securityEvent.objectType.15',
  16: 'securityEvent.objectType.16',
  17: 'securityEvent.objectType.17',
  18: 'securityEvent.objectType.18',
  19: 'securityEvent.objectType.19',
  20: 'securityEvent.objectType.20',
  21: 'securityEvent.objectType.21',
}

const OBJECT_TYPE_LABEL: Record<number, string> = {
  1: 'securityEvent.objectType.1',
  2: 'securityEvent.objectType.2',
  3: 'securityEvent.objectType.3',
  4: 'securityEvent.objectType.4',
  5: 'securityEvent.objectType.5',
  6: 'securityEvent.objectType.6',
  7: 'securityEvent.objectType.7',
  10: 'securityEvent.objectType.10',
  16: 'securityEvent.objectType.16',
  21: 'securityEvent.objectType.21',
}

const PROCESS_ACTION_LABEL: Record<number, string> = {
  1: 'securityEvent.action.process.create',
  2: 'securityEvent.action.process.terminate',
  3: 'securityEvent.action.process.createRemoteThread',
}

const FILE_ACTION_LABEL: Record<number, string> = {
  1: 'securityEvent.action.file.create',
  2: 'securityEvent.action.file.delete',
  3: 'securityEvent.action.file.modify',
  4: 'securityEvent.action.file.read',
}

/**
 * 云查状态 scan_result → 威胁状态 ID 映射
 * 0  (notFoundThreat / 未发现威胁)
 * 2 (knownSafe / 已知安全)
 * 3 (knownThreat / 已知威胁)
 * 1 (suspectedThreat / 疑似威胁)
 */
const SCAN_RESULT_LABEL: Record<number, string> = {
  0: 'parseStatus.notFoundThreat',
  2: 'parseStatus.knownSafe',
  3: 'parseStatus.knownThreat',
  1: 'parseStatus.suspectedThreat',
}

/** parseStatus i18n key → StatusColor 颜色值，供云查状态 tag 渲染共用 */
export const PARSE_STATUS_COLOR_MAP: Record<string, string> = {
  'parseStatus.notFoundThreat': StatusColor[0],
  'parseStatus.knownSafe': StatusColor[2],
  'parseStatus.knownThreat': StatusColor[3],
  'parseStatus.suspectedThreat': StatusColor[1],
  'parseStatus.unknownInfo': StatusColor[4],
}

function isDetailObjectRef (v: unknown): v is SafeEventDetailOperatorData | SafeEventDetailTarget {
  return !!v && typeof v === 'object' && 'object_type' in v && 'raw_data' in v
}

function isSdkProcessData (v: unknown): v is SdkObjectData {
  return !!v && typeof v === 'object' && !isDetailObjectRef(v)
}

function looksLikeJsonString (value?: string): boolean {
  if (!value?.trim()) return false
  const t = value.trim()
  return t.startsWith('{') && t.endsWith('}')
}

function pickString (...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v == null || v === '') continue
    const s = String(v)
    if (looksLikeJsonString(s)) continue
    return s
  }
  return undefined
}

function pickNumber (...vals: unknown[]): number | undefined {
  for (const v of vals) {
    const n = Number(v)
    if (Number.isFinite(n)) return n
  }
  return undefined
}

function pickBoolean (v: unknown): boolean | undefined {
  if (v === true || v === 'true' || v === 1 || v === '1') return true
  if (v === false || v === 'false' || v === 0 || v === '0') return false
  return undefined
}

function parseRawDataJson (raw?: string): Record<string, unknown> {
  if (!raw?.trim() || !looksLikeJsonString(raw)) return {}
  try {
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    /* ignore */
  }
  return {}
}

function normalizedSdkFromParsed (
  parsed: Record<string, unknown>,
  ref?: Pick<SafeEventDetailOperatorData | SafeEventDetailTarget, 'md5' | 'raw_data' | 'local_path'>
): SdkObjectData {
  const rawFallback = ref?.raw_data && !looksLikeJsonString(ref.raw_data) ? ref.raw_data : undefined
  return {
    pid: pickNumber(parsed.pid),
    command_line: pickString(parsed.command_line, parsed.cmdline, rawFallback),
    image_path: pickString(parsed.image_path, parsed.process_path, parsed.path, ref?.local_path),
    file_path: pickString(parsed.file_path, ref?.local_path),
    md5_hash: pickString(parsed.md5_hash, parsed.md5, ref?.md5),
    signer: pickString(parsed.signer, parsed.signature),
    signature_valid: pickBoolean(parsed.signature_valid),
    scan_result: pickNumber(parsed.scan_result),
    virus_name: pickString(parsed.virus_name),
    creation_time: pickString(parsed.creation_time, parsed.create_time),
    file_size: pickNumber(parsed.file_size),
    user: pickString(parsed.user),
    key_name: pickString(parsed.key_name),
    value_name: pickString(parsed.value_name),
    value_data: pickString(parsed.value_data),
    url: pickString(parsed.url),
    dest_ip: pickString(parsed.dest_ip),
    src_ip: pickString(parsed.src_ip),
    name: pickString(parsed.name),
  }
}

function normalizeSdkObjectData (data: SdkObjectData): SdkObjectData {
  const merged: Record<string, unknown> = { ...data }
  for (const key of ['command_line', 'image_path', 'file_path'] as const) {
    const extra = parseRawDataJson(String(data[key] ?? ''))
    Object.assign(merged, extra)
  }
  return {
    ...data,
    ...normalizedSdkFromParsed(merged),
  }
}

function asObjectArray (value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function normalizeProcessChainList (chain: unknown[]): SdkObjectData[] {
  return asObjectArray(chain).map((item) => {
    if (isDetailObjectRef(item)) return detailRefToSdkData(item)
    return normalizeSdkObjectData(item as SdkObjectData)
  })
}

function hasDisplayableFields (data: SdkObjectData): boolean {
  return !!(
    data.image_path ||
    data.file_path ||
    data.command_line ||
    data.md5_hash ||
    data.pid != null ||
    data.url ||
    data.dest_ip
  )
}

function resolveOperatorForDisplay (
  operatorData: SdkObjectData | undefined,
  operatorProcessChain: SdkObjectData[],
  apiChain?: SafeEventDetailProcessChain[]
): SdkObjectData | undefined {
  if (operatorData && hasDisplayableFields(operatorData)) return operatorData
  for (let i = operatorProcessChain.length - 1; i >= 0; i--) {
    if (hasDisplayableFields(operatorProcessChain[i])) return operatorProcessChain[i]
  }
  if (apiChain?.length) {
    const parent = apiChain.find((n) => Number(n.level) === 0) ?? apiChain[apiChain.length - 1]
    const parsed = parseRawDataJson(parent.raw_data)
    return {
      pid: pickNumber(parent.pid, parsed.pid),
      command_line: pickString(parsed.command_line, parsed.command),
      image_path: pickString(parent.local_path, parsed.image_path, parsed.file_path, parsed.localpath),
      md5_hash: pickString(parent.md5, parsed.md5_hash, parsed.md5),
      user: pickString(parsed.user),
    }
  }
  return operatorData
}

function parseEventModel (raw?: SdkSecurityEventModel | string): SdkSecurityEventModel | undefined {
  if (!raw) return undefined
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as SdkSecurityEventModel
    } catch {
      return undefined
    }
  }
  return raw
}

function resolveSdkPayload (res: SafeEventDetailApiResponse): {
  operatorData?: SdkObjectData
  operatorObjectType?: number
  operatorProcessChain: SdkObjectData[]
  targets: SdkTargetItem[]
  threat?: SdkThreat
} {
  const embedded = parseEventModel(res.event_model)
  let operatorObjectType: number | undefined
  const operatorData = (() => {
    if (embedded?.operator_data) {
      operatorObjectType = embedded.operator_type
      return normalizeSdkObjectData(embedded.operator_data)
    }
    if (isSdkProcessData(res.operator_data)) {
      return normalizeSdkObjectData(res.operator_data)
    }
    if (isDetailObjectRef(res.operator_data)) {
      operatorObjectType = Number(res.operator_data.object_type) || 1
      return detailRefToSdkData(res.operator_data)
    }
    return undefined
  })()

  const operatorProcessChain = normalizeProcessChainList(
    embedded?.operator_process_chain ?? res.operator_process_chain
  )

  const targets: SdkTargetItem[] = (() => {
    const embeddedTargets = asObjectArray(embedded?.targets)
    if (embeddedTargets.length) {
      return embeddedTargets.map((item) => {
        const target = item as SdkTargetItem
        return {
          ...target,
          data: normalizeSdkObjectData(target.data),
        }
      })
    }
    const apiTargets = asObjectArray(res.targets)
    if (!apiTargets.length) return []
    return apiTargets.map((item) => {
      if (isDetailObjectRef(item)) {
        return {
          type: Number(item.object_type) || 1,
          action: Number((item as SafeEventDetailTarget).action) || 1,
          data: detailRefToSdkData(item),
        }
      }
      return {
        ...(item as SdkTargetItem),
        data: normalizeSdkObjectData((item as SdkTargetItem).data),
      }
    })
  })()

  const threat = embedded?.threat ?? res.threat

  return { operatorData, operatorObjectType, operatorProcessChain, targets, threat }
}

function detailRefToSdkData (ref: SafeEventDetailOperatorData | SafeEventDetailTarget): SdkObjectData {
  const parsed = parseRawDataJson(ref.raw_data)
  return normalizedSdkFromParsed(parsed, ref)
}

function basenameFromPath (path?: string): string | undefined {
  if (!path || looksLikeJsonString(path)) return undefined
  return path.split(/[/\\]/).pop() || path
}

function objectTitle (type: number, data: SdkObjectData): string {
  const base =
    basenameFromPath(data.image_path) ??
    basenameFromPath(data.file_path)
  if (base) return base
  if (data.url && !looksLikeJsonString(data.url)) return String(data.url)
  if (data.key_name) return String(data.key_name)
  if (data.dest_ip) return String(data.dest_ip)
  if (data.name && !looksLikeJsonString(String(data.name))) return String(data.name)
  const cmd = data.command_line
  if (cmd && !looksLikeJsonString(cmd) && cmd.length < 120) return cmd
  return OBJECT_TYPE_LABEL[type] ?? `对象(${type})`
}

function actionLabel (type: number, action: number): string {
  if (type === 1) return PROCESS_ACTION_LABEL[action] ?? `行为(${action})`
  if (type === 2) return FILE_ACTION_LABEL[action] ?? `行为(${action})`
  return `行为(${action})`
}

function sdkFieldsToTargetFields (data: SdkObjectData): TargetObjectFields {
  const clean = (v?: string) => (v && !looksLikeJsonString(v) ? v : undefined)
  return {
    pid: data.pid != null ? String(data.pid) : undefined,
    command_line: clean(data.command_line),
    process_path: clean(data.image_path ?? data.file_path),
    md5: clean(data.md5_hash),
    signature: clean(data.signer),
    signature_valid:
      data.signature_valid === true ? '有效' : data.signature_valid === false ? '无效' : undefined,
    value_name: clean(data.value_name ?? data.key_name),
    value_data: clean(data.value_data),
  }
}

function mapTargetObjects (
  operatorData: SdkObjectData | undefined,
  operatorObjectType: number | undefined,
  targets: SdkTargetItem[],
  threat?: SdkThreat,
  apiChain?: SafeEventDetailProcessChain[],
  operatorProcessChain: SdkObjectData[] = []
): TargetObjectItem[] {
  const items: TargetObjectItem[] = []
  const displayOperator = resolveOperatorForDisplay(
    operatorData,
    operatorProcessChain,
    apiChain
  )
  if (displayOperator) {
    const threatId = threat?.identifier
    const type = operatorObjectType ?? 1
    const title = objectTitle(type, displayOperator)
    items.push({
      role: 'source',
      title,
      behavior: actionLabel(type, 1),
      is_threat_entity: !!threatId && [title, displayOperator.md5_hash, displayOperator.image_path]
        .some((v) => v && String(v).includes(threatId)),
      fields: sdkFieldsToTargetFields(displayOperator),
    })
  }
  const firstTarget = targets[0]
  if (firstTarget) {
    items.push({
      role: 'target',
      title: objectTitle(firstTarget.type, firstTarget.data),
      behavior: actionLabel(firstTarget.type, firstTarget.action),
      fields: sdkFieldsToTargetFields(firstTarget.data),
    })
  }
  return items
}

function sdkProcessToChainNode (
  data: SdkObjectData,
  id: string,
  parentId: string,
  opts?: Partial<ProcessChainNode>
): ProcessChainNode {
  const path = data.image_path ?? data.file_path ?? ''
  const name = path ? path.split(/[/\\]/).pop() || path : data.command_line || id
  return {
    id,
    name,
    pid: data.pid,
    path,
    md5: data.md5_hash,
    parent_id: parentId || undefined,
    command_line: data.command_line,
    signature: data.signer,
    signature_valid:
      data.signature_valid === true ? '有效' : data.signature_valid === false ? '无效' : undefined,
    cloud_status: data.scan_result != null ? SCAN_RESULT_LABEL[data.scan_result] : undefined,
    create_time: data.creation_time,
    file_size: data.file_size != null ? String(data.file_size) : undefined,
    username: data.user,
    threat_type: data.virus_name,
    raw_fields: data,
    ...opts,
  }
}

const OBJECT_FIELD_ORDER: Record<number, string[]> = {
  1: ['pid', 'command_line', 'image_path', 'md5_hash', 'signature_valid', 'signer', 'scan_result', 'virus_name', 'creation_time', 'file_size', 'user', 'user_groups'],
  2: ['file_path', 'creation_time', 'md5_hash', 'signature_valid', 'signer', 'scan_result', 'virus_name'],
  3: ['key_name', 'value_name', 'value_data'],
  4: ['image_path', 'md5_hash', 'signature_valid', 'signer', 'scan_result', 'virus_name'],
  5: ['name', 'image_path', 'md5_hash', 'signature_valid', 'signer', 'scan_result', 'virus_name'],
  6: ['url'],
  7: ['src_ip', 'src_port', 'dest_ip', 'dest_port', 'network_direction', 'protocol'],
  8: ['cve', 'name', 'type'],
  10: ['name'],
  11: ['auth_service', 'start_time', 'end_time', 'users', 'repeat_times'],
  12: ['subtype', 'name', 'command', 'extra', 'islocal'],
  13: ['type', 'functionname', 'args', 'scriptcontent', 'md5_hash', 'localpath'],
  14: ['dns_domain', 'dns_data', 'dns_duration', 'dns_type'],
  15: ['base_addr_i64', 'memory_size_i64', 'memory_protect', 'localpath', 'md5_hash'],
  16: ['ip'],
  17: ['taskname', 'triggername', 'command', 'argument'],
  18: ['src_pid', 'src_tid', 'tgt_pid', 'tgt_tid', 'base_addr_i64', 'memory_size_i64', 'start_address64', 'start_address_rva', 'start_type'],
  19: ['dump_path', 'dump_md5', 'dump_size', 'rules_id', 'rules_tag', 'vrius_name', 'pe_time', 'pe_pdb', 'pe_export_name', 'oep_data', 'stack_module_path', 'stack_module_md5', 'stack_frames'],
  20: ['src_ip', 'dest_ip', 'src_port', 'dest_port', 'network_direction', 'protocol', 'bytes_sent', 'bytes_received', 'packets_sent', 'packets_received', 'pcap_path', 'description'],
  21: ['name', 'domain', 'sid'],
}

const OBJECT_FIELD_LABEL: Record<string, string> = {
  pid: 'securityEvent.objField.pid',
  command_line: 'securityEvent.objField.command_line',
  image_path: 'securityEvent.objField.image_path',
  file_path: 'securityEvent.objField.file_path',
  md5_hash: 'securityEvent.objField.md5_hash',
  signature_valid: 'securityEvent.objField.signature_valid',
  signer: 'securityEvent.objField.signer',
  scan_result: 'securityEvent.objField.scan_result',
  virus_name: 'securityEvent.objField.virus_name',
  creation_time: 'securityEvent.objField.creation_time',
  file_size: 'securityEvent.objField.file_size',
  user: 'securityEvent.objField.user',
  user_groups: 'securityEvent.objField.user_groups',
  key_name: 'securityEvent.objField.key_name',
  value_name: 'securityEvent.objField.value_name',
  value_data: 'securityEvent.objField.value_data',
  name: 'securityEvent.objField.name',
  url: 'securityEvent.objField.url',
  src_ip: 'securityEvent.objField.src_ip',
  src_port: 'securityEvent.objField.src_port',
  dest_ip: 'securityEvent.objField.dest_ip',
  dest_port: 'securityEvent.objField.dest_port',
  network_direction: 'securityEvent.objField.network_direction',
  protocol: 'securityEvent.objField.protocol',
  cve: 'securityEvent.objField.cve',
  type: 'securityEvent.objField.type',
  auth_service: 'securityEvent.objField.auth_service',
  start_time: 'securityEvent.objField.start_time',
  end_time: 'securityEvent.objField.end_time',
  users: 'securityEvent.objField.users',
  repeat_times: 'securityEvent.objField.repeat_times',
  subtype: 'securityEvent.objField.subtype',
  command: 'securityEvent.objField.command',
  extra: 'securityEvent.objField.extra',
  islocal: 'securityEvent.objField.islocal',
  functionname: 'securityEvent.objField.functionname',
  args: 'securityEvent.objField.args',
  scriptcontent: 'securityEvent.objField.scriptcontent',
  localpath: 'securityEvent.objField.localpath',
  dns_domain: 'securityEvent.objField.dns_domain',
  dns_data: 'securityEvent.objField.dns_data',
  dns_duration: 'securityEvent.objField.dns_duration',
  dns_type: 'securityEvent.objField.dns_type',
  base_addr_i64: 'securityEvent.objField.base_addr_i64',
  memory_size_i64: 'securityEvent.objField.memory_size_i64',
  memory_protect: 'securityEvent.objField.memory_protect',
  ip: 'securityEvent.objField.ip',
  taskname: 'securityEvent.objField.taskname',
  triggername: 'securityEvent.objField.triggername',
  argument: 'securityEvent.objField.argument',
  src_pid: 'securityEvent.objField.src_pid',
  src_tid: 'securityEvent.objField.src_tid',
  tgt_pid: 'securityEvent.objField.tgt_pid',
  tgt_tid: 'securityEvent.objField.tgt_tid',
  start_address64: 'securityEvent.objField.start_address64',
  start_address_rva: 'securityEvent.objField.start_address_rva',
  start_type: 'securityEvent.objField.start_type',
  dump_path: 'securityEvent.objField.dump_path',
  dump_md5: 'securityEvent.objField.dump_md5',
  dump_size: 'securityEvent.objField.dump_size',
  rules_id: 'securityEvent.objField.rules_id',
  rules_tag: 'securityEvent.objField.rules_tag',
  vrius_name: 'securityEvent.objField.vrius_name',
  pe_time: 'securityEvent.objField.pe_time',
  pe_pdb: 'securityEvent.objField.pe_pdb',
  pe_export_name: 'securityEvent.objField.pe_export_name',
  oep_data: 'securityEvent.objField.oep_data',
  stack_module_path: 'securityEvent.objField.stack_module_path',
  stack_module_md5: 'securityEvent.objField.stack_module_md5',
  stack_frames: 'securityEvent.objField.stack_frames',
  bytes_sent: 'securityEvent.objField.bytes_sent',
  bytes_received: 'securityEvent.objField.bytes_received',
  packets_sent: 'securityEvent.objField.packets_sent',
  packets_received: 'securityEvent.objField.packets_received',
  pcap_path: 'securityEvent.objField.pcap_path',
  description: 'securityEvent.objField.description',
  domain: 'securityEvent.objField.domain',
  sid: 'securityEvent.objField.sid',
}

const NETWORK_DIRECTION_KEY: Record<number, string> = {
  1: 'securityEvent.networkDirection.outbound',
  2: 'securityEvent.networkDirection.inbound',
}

const LOCAL_COMMAND_KEY: Record<number, string> = {
  1: 'securityEvent.localCommand.local',
  2: 'securityEvent.localCommand.remote',
}

const PROTOCOL_KEY: Record<number, string> = {
  1: 'ipAddressBlocking.protocolAny',
  2: 'TCP',
  3: 'UDP',
}

function formatObjectFieldValue (key: string, value: unknown, objectType?: number): string {
  if (Array.isArray(value)) return value.join(', ')
  if (key === 'signature_valid') return String(value) === 'true' ? 'securityEvent.signatureValid.true' : 'securityEvent.signatureValid.false'
  if (key === 'scan_result') return SCAN_RESULT_LABEL[Number(value)] ?? String(value)
  if (key === 'network_direction') return NETWORK_DIRECTION_KEY[Number(value)] ?? String(value)
  if (key === 'islocal') return LOCAL_COMMAND_KEY[Number(value)] ?? String(value)
  if (key === 'protocol') return PROTOCOL_KEY[Number(value)] ?? String(value)
  if (key === 'type' && objectType === 13) return SCRIPT_TYPE_LABEL[Number(value)] ?? String(value)
  return String(value)
}

/** 脚本子类型（object_type=13 时 raw_data.type 的值映射） */
const SCRIPT_TYPE_LABEL: Record<number, string> = {
  1: 'securityEvent.scriptType.powershell',
  2: 'securityEvent.scriptType.wscript',
  3: 'securityEvent.scriptType.vba',
  4: 'securityEvent.scriptType.cmd',
}

function buildDisplayRawFields (
  objectType: number,
  parsed: Record<string, unknown>,
  node: SafeEventDetailProcessChain
): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  const order = OBJECT_FIELD_ORDER[objectType] ?? Object.keys(parsed)
  order.forEach((key) => {
    const value = parsed[key]
    if (value == null || value === '') return
    fields[OBJECT_FIELD_LABEL[key] ?? key] = formatObjectFieldValue(key, value, objectType)
  })
  const extraKeys = Object.keys(parsed).filter((key) => !order.includes(key))
  extraKeys.forEach((key) => {
    const value = parsed[key]
    if (value == null || value === '') return
    fields[OBJECT_FIELD_LABEL[key] ?? key] = formatObjectFieldValue(key, value, objectType)
  })
  fields['securityEvent.objField.objectType'] = `securityEvent.objectType.${objectType}`
  fields['securityEvent.objField.action'] = getActionName(objectType, node.action || 1)
  return fields
}

export function mapProcessChainFromSdk (
  operatorData: SdkObjectData | undefined,
  operatorProcessChain: SdkObjectData[],
  threat?: SdkThreat,
  apiChain?: SafeEventDetailProcessChain[]
): ProcessChainNode[] {
  if (apiChain?.length) {
    const threatId = threat?.identifier

    return apiChain.map((node, index) => {
      const objectType = Number(node.object_type) || 1
      const parsed = parseRawDataJson(node.raw_data)
      const path = pickString(node.local_path, parsed.image_path, parsed.file_path, parsed.localpath) ?? ''
      const name =
        basenameFromPath(path) ??
        OBJECT_TYPE_KEY[objectType] ??
        pickString(parsed.name, parsed.taskname, parsed.key_name, parsed.url, parsed.ip) ??
        `securityEvent.objectType.${objectType}`
      const md5 = pickString(node.md5, parsed.md5_hash, parsed.md5, parsed.dump_md5)
      const signatureValid = pickBoolean(parsed.signature_valid)
      const scanResult = pickNumber(parsed.scan_result)
      const commandLine = pickString(parsed.command_line, parsed.command)
      const edgeAction = Number(node.action) || 1
      const edgeObjectType = objectType
      const isThreat =
        !!threatId &&
        [path, md5, name, commandLine].some((v) => v && String(v).includes(threatId))

      // data_type → node_type: 1=操作对象→发起者(2), 2=目标对象→目标(3)
      const resolvedNodeType =
        node.node_type === NodeType.Operator || node.node_type === NodeType.Target
          ? node.node_type
          : node.data_type === 1
            ? NodeType.Operator
            : node.data_type === 2
              ? NodeType.Target
              : node.node_type

      return {
        id: pickString(node.id, node.pid, node.business_id) || String(index),
        name,
        pid: pickNumber(node.pid, parsed.pid),
        path,
        md5,
        parent_id: pickString(node.parent_id) || undefined,
        business_id: node.business_id || undefined,
        command_line: commandLine,
        username: pickString(node.user, parsed.user),
        signature: pickString(parsed.signer, parsed.signature),
        signature_valid:
          signatureValid === true ? '有效' : signatureValid === false ? '无效' : undefined,
        cloud_status: scanResult != null ? SCAN_RESULT_LABEL[scanResult] : undefined,
        create_time: pickString(parsed.creation_time, parsed.create_time),
        file_size: parsed.file_size != null ? String(parsed.file_size) : undefined,
        threat_type: pickString(parsed.virus_name),
        node_type: resolvedNodeType,
        object_type: node.object_type,
        action: edgeAction,
        action_label: getActionName(edgeObjectType, edgeAction),
        behavior: getActionName(edgeObjectType, edgeAction),
        raw_fields: buildDisplayRawFields(objectType, parsed, node),
        is_threat: isThreat || resolvedNodeType === NodeType.Target,
        is_suspicious: scanResult === 3,
        is_threat_entity: node.is_threat_entity,
        download_url: node.download_url || undefined,
        has_more: node.has_more,
        top: node.top,
        device_id: node.device_id,
      }
    })
  }

  if (!operatorData && !operatorProcessChain.length) return []

  // SDK：chain[0]=直接父进程 … chain[n-1]=根；operator_data=当前进程
  const ancestors = [...operatorProcessChain]
  const nodes: ProcessChainNode[] = []
  const ids: string[] = []

  ancestors.forEach((proc, index) => {
    const id = `pc-a-${index}`
    ids.push(id)
    const parentId = index < ancestors.length - 1 ? `pc-a-${index + 1}` : ''
    nodes.push(sdkProcessToChainNode(proc, id, parentId))
  })

  const currentId = 'pc-current'
  const parentId = ancestors.length ? 'pc-a-0' : ''
  const currentPath = operatorData?.image_path ?? operatorData?.file_path ?? ''
  const threatId = threat?.identifier
  const isThreat =
    !!threatId &&
    [currentPath, operatorData?.command_line, operatorData?.md5_hash].some(
      (v) => v && String(v).includes(threatId)
    )

  if (operatorData) {
    nodes.unshift(
      sdkProcessToChainNode(operatorData, currentId, parentId, {
        is_threat: isThreat,
        is_suspicious: operatorData.scan_result === 3,
        threat_type: operatorData.virus_name,
      })
    )
  }

  return nodes
}

export function mapThreatEntityType (type?: number): ThreatEntityType | undefined {
  if (type == null) return undefined
  return API_THREAT_TYPE_TO_ENTITY[type as ApiThreatType]
}

/** 从威胁实体字符串推断 API threat_type（列表/详情未返回时的兜底） */
export function inferApiThreatTypeFromEntity (entity?: string): ApiThreatType | undefined {
  if (!entity?.trim()) return undefined
  const e = entity.trim()
  if (/^https?:\/\//i.test(e)) return ApiThreatType.Url
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(e)) return ApiThreatType.Ip
  if (/^[a-f0-9]{32}$/i.test(e)) return ApiThreatType.BehaviorHash
  if (/^[a-z0-9][-a-z0-9.]*\.[a-z]{2,}(\/|$)/i.test(e) && !e.includes('\\')) {
    return ApiThreatType.Url
  }
  if (/[/\\]/.test(e) || /\.(exe|dll|sys|bat|cmd|msi|scr|com|pif)$/i.test(e)) {
    return ApiThreatType.File
  }
  return undefined
}

/** 优先使用列表/详情 threat_type，避免 SDK threat.type 与接口 1–6 不一致 */
export function resolveApiThreatType (
  base?: Partial<SecurityEventListItem>,
  threat?: SdkThreat,
  detailThreatType?: number
): ApiThreatType | undefined {
  if (
    detailThreatType != null &&
    API_THREAT_TYPE_TO_ENTITY[detailThreatType as ApiThreatType] != null
  ) {
    return detailThreatType as ApiThreatType
  }
  const fromList = base?.threat_type
  if (fromList != null && API_THREAT_TYPE_TO_ENTITY[fromList as ApiThreatType] != null) {
    return fromList as ApiThreatType
  }
  const fromSdk = threat?.type
  if (fromSdk != null && API_THREAT_TYPE_TO_ENTITY[fromSdk as ApiThreatType] != null) {
    return fromSdk as ApiThreatType
  }
  return inferApiThreatTypeFromEntity(base?.threat_entity ?? threat?.identifier)
}

export { deriveDisposeFlags, parseHandleResultCodes } from './handle-result'

function mapSdkProtocolToBlock (protocol?: number): number {
  if (protocol === 6) return BlockProtocol.Tcp
  if (protocol === 17) return BlockProtocol.Udp
  return BlockProtocol.Any
}

function mapNetworkDirectionToBlock (direction?: number): number {
  if (direction === 1) return BlockDirection.Inbound
  if (direction === 2) return BlockDirection.Outbound
  return BlockDirection.Bidirectional
}

function resolveEventOperator (
  operatorData: SdkObjectData | undefined,
  targetObjects: TargetObjectItem[],
  base?: Partial<SecurityEventListItem>
): string | undefined {
  if (operatorData?.src_ip) return String(operatorData.src_ip)
  if (operatorData?.dest_ip) return String(operatorData.dest_ip)
  if (operatorData?.user) return String(operatorData.user)
  const source = targetObjects.find((item) => item.role === 'source')
  if (source?.title) return source.title
  if (base?.system_ip) return String(base.system_ip)
  if (base?.client_ip) return String(base.client_ip)
  return undefined
}

function enrichThreatFields (
  entityType: ThreatEntityType | undefined,
  threat: SdkThreat | undefined,
  operatorData: SdkObjectData | undefined,
  threatNode: ProcessChainNode | undefined,
  eventDesc?: string,
  targetObjects: TargetObjectItem[] = [],
  base?: Partial<SecurityEventListItem>,
  networkDirection?: number
) {
  const identifier = threat?.identifier ?? ''
  // 优先从威胁实体节点（is_threat_entity=1）的 raw_data 取值
  let process_pid = threatNode?.pid ?? operatorData?.pid
  let process_path = threatNode?.path ?? operatorData?.image_path
  let file_md5 = threatNode?.md5 ?? operatorData?.md5_hash
  // file_path 优先取威胁实体节点的 path（即 image_path/file_path）
  let file_path = threatNode?.path ?? operatorData?.file_path ?? operatorData?.image_path
  const process_command_line = threatNode?.command_line ?? operatorData?.command_line
  let file_name: string | undefined
  let file_threat_status: string | undefined
  if (file_path) {
    const baseName = file_path.split(/[/\\]/).pop()
    file_name = baseName || undefined
  }
  // 威胁状态：优先取威胁实体节点的 scan_result（cloud_status）/ virus_name（threat_type）
  if (threatNode?.threat_type) {
    file_threat_status = threatNode.threat_type
  } else if (threatNode?.cloud_status) {
    file_threat_status = threatNode.cloud_status
  } else if (entityType === ThreatEntityType.File) {
    if (operatorData?.virus_name) {
      file_threat_status = String(operatorData.virus_name)
    } else if (operatorData?.scan_result != null) {
      file_threat_status = SCAN_RESULT_LABEL[operatorData.scan_result]
    }
  }
  let trust_display_text = identifier

  if (entityType === ThreatEntityType.Process) {
    if (identifier) {
      process_path = process_path || identifier
      if (!process_pid && /^\d+$/.test(identifier)) {
        process_pid = Number(identifier)
      }
    }
    trust_display_text = [process_path, process_pid != null ? `PID ${process_pid}` : '']
      .filter(Boolean)
      .join(' · ')
  } else if (entityType === ThreatEntityType.File) {
    if (identifier) {
      file_path = file_path || identifier
      if (identifier.length === 32 && /^[a-f0-9]+$/i.test(identifier)) {
        file_md5 = file_md5 || identifier
      }
    }
    trust_display_text = [file_path, file_md5 ? `MD5 ${file_md5}` : '']
      .filter(Boolean)
      .join(' · ')
  } else if (entityType === ThreatEntityType.BehaviorHash) {
    trust_display_text = identifier || eventDesc || ''
  } else if (entityType === ThreatEntityType.User) {
    trust_display_text = identifier || operatorData?.user || ''
  }

  return {
    process_pid,
    process_path,
    process_command_line,
    file_md5,
    file_path,
    file_name,
    file_threat_status,
    trust_display_text,
    event_operator: resolveEventOperator(operatorData, targetObjects, base),
    default_block_protocol: mapSdkProtocolToBlock(operatorData?.protocol),
    default_block_direction: mapNetworkDirectionToBlock(networkDirection ?? operatorData?.network_direction),
  }
}

function buildDescription (
  res: SafeEventDetailApiResponse,
  threat?: SdkThreat,
  base?: Partial<SecurityEventListItem>
): string | undefined {
  // 优先从 event_id 映射（与列表事件描述一致，如 1000 → "恶意进程执行"）
  const descFromId = getEventDescByEventId(base?.event_id)
  if (descFromId) return descFromId
  // attack_tactic 映射（如 TA0001 → "初始访问"）
  const tacticName = getAttackTacticName(res.attack_tactic)
  if (tacticName) return tacticName
  // API 原始 event_desc
  if (res.event_desc) return res.event_desc
  const parts = [res.attack_technique, res.attack_subtechnique].filter(Boolean)
  if (parts.length) return parts.join(' / ')
  return threat?.identifier ?? base?.threat_entity
}

/** TerminalHead 图标索引 0–4；NaN/越界会导致 TerminalIconRound[n][loginStatus] 抛错 */
function normalizeTerminalSystemType (raw?: string | number): number {
  const n = Number(raw)
  if (Number.isFinite(n) && n >= 0 && n <= 4) return n
  return 1
}

function normalizeFiniteNumber (raw?: string | number, fallback = 0): number {
  const n = Number(raw)
  return Number.isFinite(n) ? n : fallback
}

/** 将 /safeevent/detail 响应映射为详情页 UI 模型 */
export function mapSafeEventDetailResponse (
  res: SafeEventDetailApiResponse,
  businessId: string,
  base?: Partial<SecurityEventListItem>
): SecurityEventDetail {
  const { operatorData, operatorObjectType, operatorProcessChain, targets, threat } =
    resolveSdkPayload(res)
  const displayOperator = resolveOperatorForDisplay(
    operatorData,
    operatorProcessChain
  )
  const targetObjects = mapTargetObjects(
    operatorData,
    operatorObjectType,
    targets,
    threat,
    undefined,
    operatorProcessChain
  )
  const apiThreatType = resolveApiThreatType(base, threat, res.threat_type)
  const threatEntityType = mapThreatEntityType(apiThreatType)
  const eventDesc = buildDescription(res, threat, base)
  const threatFields = enrichThreatFields(
    threatEntityType,
    threat,
    displayOperator,
    undefined,
    eventDesc,
    targetObjects,
    base,
    res.network_direction
  )
  const disposeFlags = deriveDisposeFlags(
    base?.handle_result ?? res.handle_result
  )

  const eventOperator = threatFields.event_operator

  return {
    ...(base as SecurityEventDetail | undefined),
    business_id: businessId,
    event_id: base?.event_id ?? 0,
    event_type: base?.event_type ?? '',
    threat_entity: base?.threat_entity ?? threat?.identifier ?? '',
    computer_name: base?.computer_name ?? '',
    device_id: base?.device_id ?? '',
    group_name: base?.group_name ?? '',
    user_mark: base?.user_mark ?? '',
    system_ip: base?.system_ip ?? '',
    client_ip: base?.client_ip ?? '',
    event_level: base?.event_level ?? 0,
    handle_status: base?.handle_status ?? SecurityDisposeStatus.Pending,
    handle_result: base?.handle_result ?? res.handle_result ?? 0,
    first_alarm_time: res.first_alarm_time ?? base?.first_alarm_time ?? 0,
    last_alarm_time: res.last_alarm_time ?? base?.last_alarm_time ?? 0,
    alarm_count: res.alarm_count ?? base?.alarm_count ?? 0,
    attack_tactic: res.attack_tactic ?? base?.attack_tactic ?? '',
    event_time: res.event_time,
    attack_technique: res.attack_technique,
    attack_subtechnique: res.attack_subtechnique,
    operator_data: isDetailObjectRef(res.operator_data) ? res.operator_data : undefined,
    targets: res.targets,
    log: res.log ?? [],
    user_name: res.user_name || base?.user_mark || '',
    event_desc: eventDesc,
    threat_entity_type: threatEntityType,
    api_threat_type: apiThreatType,
    target_objects: targetObjects,
    hit_attack_stages: parseHitAttackTacticIds(res.attack_tactic, base?.attack_tactic),
    ...threatFields,
    event_operator: eventOperator,
    ...disposeFlags,
    system_type: normalizeTerminalSystemType(base?.system_type),
    system_name: base?.system_name,
    isolation_status: normalizeFiniteNumber(base?.isolation_status, 0),
    login_status: base?.login_status,
  }
}
